/**
 * InventoryPanel -- HTML/CSS overlay panel for viewing equipment and inventory.
 *
 * Two sections: Equipment (left) and Bag (right).
 * Equipment shows the character's 10 equipment slots.
 * Bag shows all items in the shared party inventory.
 *
 * Supports keyboard, gamepad, and touch navigation.
 *
 * @module InventoryPanel
 */

import focusManager from '../../input/FocusManager.js';
import eventBus from '../../core/EventBus.js';
import inventorySystem, { SLOT_INFO } from '../../systems/InventorySystem.js';
import lootSystem, { RARITY_COLORS, RARITY_NAMES } from '../../systems/LootSystem.js';

/** Equipment slot display order. */
const SLOT_ORDER = [
  'weapon', 'offhand', 'head', 'chest', 'legs',
  'boots', 'gloves', 'ring1', 'ring2', 'amulet',
];

export default class InventoryPanel {
  /**
   * @param {object} options
   * @param {import('../../entities/Party.js').default} options.party
   * @param {Function} [options.onClose] - Callback when panel closes
   */
  constructor(options = {}) {
    this._party = options.party || null;
    this._onClose = options.onClose || null;
    this._el = null;
    this._tooltipEl = null;
    this._selectedCharIndex = 0;
    this._selectedBagItem = null;
    this._isOpen = false;

    // Bind event handler for cleanup
    this._onInventoryChanged = () => {
      if (this._isOpen && !this._tooltipEl) this._render();
    };
  }

  /**
   * Check if the panel is open.
   * @returns {boolean}
   */
  get isOpen() {
    return this._isOpen;
  }

  /**
   * Open the inventory panel.
   */
  open() {
    if (this._isOpen) return;

    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    this.close(); // ensure no duplicates

    this._isOpen = true;

    this._el = document.createElement('div');
    this._el.className = 'inventory-overlay';
    this._el.id = 'inventory-overlay';
    this._el.setAttribute('role', 'dialog');
    this._el.setAttribute('aria-label', 'Inventory');
    this._el.setAttribute('data-scene', 'inventory');

    this._render();
    uiRoot.appendChild(this._el);
    focusManager.pushGroup('inventory', this._el);

    // Listen for inventory changes
    eventBus.on('inventory:changed', this._onInventoryChanged);

    // Listen for cancel on this focus group
    this._el.addEventListener('focusgroup:cancelled', () => {
      this.close();
    });
  }

  /**
   * Close and remove the panel.
   */
  close() {
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
      const topGroup = focusManager._currentGroup ? focusManager._currentGroup() : null;
      if (topGroup && topGroup.name === 'inventory') {
        focusManager.popGroup();
      }
    }
    this._el = null;
    this._tooltipEl = null;
    this._selectedBagItem = null;
    this._isOpen = false;

    eventBus.off('inventory:changed', this._onInventoryChanged);

    if (this._onClose) this._onClose();
  }

  /**
   * Toggle open/close.
   */
  toggle() {
    if (this._isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  // ── Rendering ──────────────────────────────────────

  /**
   * Render the full panel contents.
   * @private
   */
  _render() {
    if (!this._el) return;
    this._el.innerHTML = '';

    const panel = document.createElement('div');
    panel.className = 'inventory-panel';

    // Header
    const header = document.createElement('div');
    header.className = 'inventory-header';

    const title = document.createElement('h2');
    title.className = 'inventory-title';
    title.textContent = 'Inventory';
    header.appendChild(title);

    // Character selector (if party has multiple members)
    const party = this._party || inventorySystem.getParty();
    if (party && party.members.length > 1) {
      const charSelect = document.createElement('div');
      charSelect.className = 'inventory-char-select';
      party.members.forEach((member, idx) => {
        const btn = document.createElement('button');
        btn.className = `inventory-char-btn${idx === this._selectedCharIndex ? ' inventory-char-btn--active' : ''}`;
        btn.setAttribute('data-focusable', 'true');
        btn.textContent = member.name;
        btn.addEventListener('click', () => {
          this._selectedCharIndex = idx;
          this._render();
          focusManager.refreshGroup();
        });
        charSelect.appendChild(btn);
      });
      header.appendChild(charSelect);
    }

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'inventory-close-btn';
    closeBtn.id = 'inventory-close-btn';
    closeBtn.setAttribute('data-focusable', 'true');
    closeBtn.textContent = 'X';
    closeBtn.setAttribute('aria-label', 'Close inventory');
    closeBtn.addEventListener('click', () => this.close());
    header.appendChild(closeBtn);

    panel.appendChild(header);

    // Content: Equipment + Bag
    const content = document.createElement('div');
    content.className = 'inventory-content';

    // Equipment section
    const equipSection = this._renderEquipmentSection(party);
    content.appendChild(equipSection);

    // Bag section
    const bagSection = this._renderBagSection();
    content.appendChild(bagSection);

    panel.appendChild(content);

    // Gold display
    if (party) {
      const goldBar = document.createElement('div');
      goldBar.className = 'inventory-gold-bar';
      goldBar.textContent = `Gold: ${party.gold}`;
      panel.appendChild(goldBar);
    }

    this._el.appendChild(panel);
  }

  /**
   * Render the equipment section.
   * @private
   */
  _renderEquipmentSection(party) {
    const section = document.createElement('div');
    section.className = 'inventory-equipment';

    const heading = document.createElement('h3');
    heading.className = 'inventory-section-heading';
    heading.textContent = 'Equipment';
    section.appendChild(heading);

    const character = party?.members[this._selectedCharIndex];

    const grid = document.createElement('div');
    grid.className = 'inventory-equipment-grid';

    for (const slot of SLOT_ORDER) {
      const slotInfo = SLOT_INFO[slot] || { name: slot, icon: '?' };
      const item = character?.equipment?.[slot] || null;

      const slotEl = document.createElement('div');
      slotEl.className = `inventory-slot${item ? ' inventory-slot--filled' : ' inventory-slot--empty'}`;
      if (item) {
        slotEl.classList.add(`inventory-rarity--${item.rarity || 'normal'}`);
      }
      slotEl.setAttribute('data-focusable', 'true');
      slotEl.setAttribute('data-slot', slot);
      slotEl.setAttribute('role', 'button');
      slotEl.setAttribute('aria-label', item ? `${slotInfo.name}: ${item.name}` : `${slotInfo.name}: Empty`);

      const slotLabel = document.createElement('span');
      slotLabel.className = 'inventory-slot-label';
      slotLabel.textContent = slotInfo.name;
      slotEl.appendChild(slotLabel);

      if (item) {
        const itemName = document.createElement('span');
        itemName.className = 'inventory-slot-item';
        itemName.style.color = RARITY_COLORS[item.rarity] || RARITY_COLORS.normal;
        itemName.textContent = item.name;
        slotEl.appendChild(itemName);
      } else {
        const empty = document.createElement('span');
        empty.className = 'inventory-slot-empty-text';
        empty.textContent = '---';
        slotEl.appendChild(empty);
      }

      slotEl.addEventListener('click', () => {
        if (item) {
          this._showTooltip(item, slotEl, slot, character);
        }
      });

      // Focus handlers for tooltip
      slotEl.addEventListener('mouseenter', () => {
        if (item) this._showTooltipPopup(item, slotEl, slot);
      });
      slotEl.addEventListener('mouseleave', () => {
        this._hideTooltipPopup();
      });

      grid.appendChild(slotEl);
    }

    section.appendChild(grid);
    return section;
  }

  /**
   * Render the bag section.
   * @private
   */
  _renderBagSection() {
    const section = document.createElement('div');
    section.className = 'inventory-bag';

    const heading = document.createElement('h3');
    heading.className = 'inventory-section-heading';
    const items = inventorySystem.getItems();
    heading.textContent = `Bag (${items.length}/${inventorySystem.getMaxBagSize()})`;
    section.appendChild(heading);

    const grid = document.createElement('div');
    grid.className = 'inventory-bag-grid';

    if (items.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'inventory-bag-empty';
      empty.textContent = 'No items in bag';
      grid.appendChild(empty);
    } else {
      for (const item of items) {
        const itemEl = document.createElement('div');
        itemEl.className = `inventory-bag-item inventory-rarity--${item.rarity || 'normal'}`;
        itemEl.setAttribute('data-focusable', 'true');
        itemEl.setAttribute('data-item-id', item.instanceId);
        itemEl.setAttribute('role', 'button');
        itemEl.setAttribute('aria-label', `${item.name} (${(RARITY_NAMES[item.rarity] || 'Normal')})`);

        const nameEl = document.createElement('span');
        nameEl.className = 'inventory-bag-item-name';
        nameEl.style.color = RARITY_COLORS[item.rarity] || RARITY_COLORS.normal;
        nameEl.textContent = item.name;
        itemEl.appendChild(nameEl);

        const slotEl = document.createElement('span');
        slotEl.className = 'inventory-bag-item-slot';
        const slotInfo = SLOT_INFO[item.slot] || SLOT_INFO[item.slot.replace(/\d+$/, '')] || { name: item.slot };
        slotEl.textContent = slotInfo.name;
        itemEl.appendChild(slotEl);

        itemEl.addEventListener('click', () => {
          this._selectedBagItem = item;
          this._showTooltip(item, itemEl, null, null);
        });

        itemEl.addEventListener('mouseenter', () => {
          this._showTooltipPopup(item, itemEl, null);
        });
        itemEl.addEventListener('mouseleave', () => {
          this._hideTooltipPopup();
        });

        grid.appendChild(itemEl);
      }
    }

    section.appendChild(grid);
    return section;
  }

  // ── Tooltip ────────────────────────────────────────

  /**
   * Show a tooltip for an item with action buttons.
   * @private
   */
  _showTooltip(item, anchorEl, equippedSlot, character) {
    this._hideTooltipPopup();

    const tooltip = document.createElement('div');
    tooltip.className = 'inventory-tooltip';
    tooltip.id = 'inventory-tooltip';
    tooltip.setAttribute('role', 'dialog');
    tooltip.setAttribute('aria-label', `Item: ${item.name}`);

    // Item name
    const name = document.createElement('div');
    name.className = 'inventory-tooltip-name';
    name.style.color = RARITY_COLORS[item.rarity] || RARITY_COLORS.normal;
    name.textContent = item.name;
    tooltip.appendChild(name);

    // Rarity + slot
    const meta = document.createElement('div');
    meta.className = 'inventory-tooltip-meta';
    const rarityName = (RARITY_NAMES[item.rarity] || 'Normal');
    const slotName = (SLOT_INFO[item.slot] || SLOT_INFO[item.slot?.replace(/\d+$/, '')] || { name: item.slot }).name;
    meta.textContent = `${rarityName} ${slotName}`;
    if (item.level) meta.textContent += ` (Lvl ${item.level})`;
    tooltip.appendChild(meta);

    // Base stats
    const stats = item.totalStats || item.baseStats || {};
    if (Object.keys(stats).length > 0) {
      const statsDiv = document.createElement('div');
      statsDiv.className = 'inventory-tooltip-stats';
      for (const [stat, value] of Object.entries(stats)) {
        const row = document.createElement('div');
        row.className = 'inventory-tooltip-stat';
        const sign = value >= 0 ? '+' : '';
        row.textContent = `${sign}${value} ${this._formatStatName(stat)}`;
        statsDiv.appendChild(row);
      }
      tooltip.appendChild(statsDiv);
    }

    // Affixes
    if (item.affixes && item.affixes.length > 0) {
      const affixDiv = document.createElement('div');
      affixDiv.className = 'inventory-tooltip-affixes';
      for (const affix of item.affixes) {
        const row = document.createElement('div');
        row.className = 'inventory-tooltip-affix';
        if (affix.description) {
          row.textContent = affix.description;
        } else if (affix.name) {
          row.textContent = affix.name;
        }
        affixDiv.appendChild(row);
      }
      tooltip.appendChild(affixDiv);
    }

    // Action buttons
    const actions = document.createElement('div');
    actions.className = 'inventory-tooltip-actions';

    const party = this._party || inventorySystem.getParty();
    const activeChar = party?.members[this._selectedCharIndex];

    if (equippedSlot && activeChar) {
      // This is an equipped item -- show Unequip button
      const unequipBtn = document.createElement('button');
      unequipBtn.className = 'inventory-tooltip-btn';
      unequipBtn.setAttribute('data-focusable', 'true');
      unequipBtn.textContent = 'Unequip';
      unequipBtn.addEventListener('click', () => {
        inventorySystem.unequipItem(activeChar, equippedSlot);
        this._hideTooltipPopup();
        this._render();
        focusManager.refreshGroup();
      });
      actions.appendChild(unequipBtn);
    } else if (activeChar) {
      // This is a bag item -- show Equip button
      const equipBtn = document.createElement('button');
      equipBtn.className = 'inventory-tooltip-btn';
      equipBtn.setAttribute('data-focusable', 'true');
      equipBtn.textContent = 'Equip';
      equipBtn.addEventListener('click', () => {
        inventorySystem.equipItem(activeChar, item);
        this._hideTooltipPopup();
        this._render();
        focusManager.refreshGroup();
      });
      actions.appendChild(equipBtn);
    }

    const closeBtn = document.createElement('button');
    closeBtn.className = 'inventory-tooltip-btn inventory-tooltip-btn--close';
    closeBtn.setAttribute('data-focusable', 'true');
    closeBtn.textContent = 'Close';
    closeBtn.addEventListener('click', () => {
      this._hideTooltipPopup();
      focusManager.refreshGroup();
    });
    actions.appendChild(closeBtn);

    tooltip.appendChild(actions);

    // Position near the anchor element
    this._el.appendChild(tooltip);
    this._tooltipEl = tooltip;

    // Push a nested focus group for the tooltip
    focusManager.pushGroup('inventory-tooltip', tooltip);
  }

  /**
   * Show a hover tooltip (no action buttons).
   * @private
   */
  _showTooltipPopup(item, anchorEl, equippedSlot) {
    // Only show hover tooltip if we're not already showing an action tooltip
    if (this._tooltipEl) return;

    const popup = document.createElement('div');
    popup.className = 'inventory-hover-tooltip';
    popup.id = 'inventory-hover-tooltip';

    const name = document.createElement('div');
    name.className = 'inventory-tooltip-name';
    name.style.color = RARITY_COLORS[item.rarity] || RARITY_COLORS.normal;
    name.textContent = item.name;
    popup.appendChild(name);

    const meta = document.createElement('div');
    meta.className = 'inventory-tooltip-meta';
    meta.textContent = `${(RARITY_NAMES[item.rarity] || 'Normal')}`;
    popup.appendChild(meta);

    const stats = item.totalStats || item.baseStats || {};
    for (const [stat, value] of Object.entries(stats)) {
      const row = document.createElement('div');
      row.className = 'inventory-tooltip-stat';
      row.textContent = `+${value} ${this._formatStatName(stat)}`;
      popup.appendChild(row);
    }

    if (item.affixes && item.affixes.length > 0) {
      for (const affix of item.affixes) {
        const row = document.createElement('div');
        row.className = 'inventory-tooltip-affix';
        row.textContent = affix.description || affix.name || '';
        popup.appendChild(row);
      }
    }

    this._el.appendChild(popup);
    this._hoverTooltip = popup;
  }

  /**
   * Hide any open tooltip.
   * @private
   */
  _hideTooltipPopup() {
    if (this._tooltipEl) {
      const topGroup = focusManager._currentGroup ? focusManager._currentGroup() : null;
      if (topGroup && topGroup.name === 'inventory-tooltip') {
        focusManager.popGroup();
      }
      if (this._tooltipEl.parentNode) {
        this._tooltipEl.parentNode.removeChild(this._tooltipEl);
      }
      this._tooltipEl = null;
    }
    if (this._hoverTooltip) {
      if (this._hoverTooltip.parentNode) {
        this._hoverTooltip.parentNode.removeChild(this._hoverTooltip);
      }
      this._hoverTooltip = null;
    }
  }

  // ── Helpers ────────────────────────────────────────

  /**
   * Format a stat key for display.
   * @private
   */
  _formatStatName(stat) {
    const names = {
      damage: 'Damage',
      defense: 'Defense',
      accuracy: 'Accuracy',
      hp: 'HP',
      mana: 'Mana',
      spellPower: 'Spell Power',
      speed: 'Speed',
      blockChance: 'Block Chance',
      str: 'STR',
      agi: 'AGI',
      int: 'INT',
      vit: 'VIT',
      wis: 'WIS',
      cha: 'CHA',
    };
    return names[stat] || stat;
  }
}
